import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Instagram, Facebook } from "lucide-react";

// Cal.com booking link
const CALENDLY_LINK = "https://cal.com/modern-path-ai-pludn4";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Top 6 high-priority industries for footer links
const priorityIndustries = [
  { name: "Appliance Repair", slug: "appliance-repair" },
  { name: "HVAC & Plumbing", slug: "home-services" },
  { name: "Law Firms", slug: "legal" },
  { name: "Dental Clinics", slug: "dental" },
  { name: "Real Estate", slug: "real-estate" },
  { name: "Auto Repair", slug: "auto-repair" },
];

const GlobalFooter = ({ variant = "full" }) => {
  // Simplified footer for industry pages
  if (variant === "simple") {
    return (
      <footer className="py-12 px-4 border-t border-white/5 bg-[#0A0A0A]" data-testid="global-footer">
        <div className="max-w-[1400px] mx-auto">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand Column */}
            <div>
              <Link to="/" className="text-xl font-bold text-white mb-4 block" style={{ fontFamily: 'Syne, sans-serif' }}>
                MODERN PATH AI
              </Link>
              <p className="text-[#A0A0A0] text-sm mb-4 leading-relaxed">
                Custom AI Voice Agents & Business Automation Solutions for service businesses across North America.
              </p>
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.facebook.com/people/Modern-Path-AI/61583886751593/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.instagram.com/modern_path_ai/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Our Solutions Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">Our Solutions</h4>
              <ul className="space-y-2">
                {priorityIndustries.map((industry) => (
                  <li key={industry.slug}>
                    <Link 
                      to={`/industries/${industry.slug}`}
                      className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                    >
                      AI for {industry.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <a 
                    href={CALENDLY_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                  >
                    Book a Demo
                  </a>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="mailto:info@modernpathai.com" 
                    className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    info@modernpathai.com
                  </a>
                </li>
                <li>
                  <a 
                    href="tel:+16473707545" 
                    className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    647-370-7545
                  </a>
                </li>
                <li className="text-sm text-[#606060]">
                  Toronto, Ontario, Canada
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#606060] text-sm">
              © {new Date().getFullYear()} Modern Path AI Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-[#606060] hover:text-[#00FFFF] transition-colors">
                Privacy Policy
              </Link>
              <a href="/sitemap.xml" className="text-sm text-[#606060] hover:text-[#00FFFF] transition-colors">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Full footer for homepage
  return (
    <footer className="py-20 px-4 border-t border-white/5 bg-[#0A0A0A]" data-testid="global-footer">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10"
        >
          {/* Brand Column */}
          <motion.div variants={fadeUp} className="lg:col-span-2">
            <Link to="/" className="text-2xl font-bold text-white mb-4 block" style={{ fontFamily: 'Syne, sans-serif' }}>
              MODERN PATH AI
            </Link>
            <p className="text-[#A0A0A0] mb-6 leading-relaxed max-w-sm">
              Stop missing calls. Start closing leads. Custom AI Voice Agents & Business Automation for service businesses across North America.
            </p>
            
            {/* CTA */}
            <a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#00FFFF] text-[#0A0A0A] px-6 py-3 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-white transition-colors mb-6"
            >
              Book Your Free Demo
            </a>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a 
                href="https://www.facebook.com/people/Modern-Path-AI/61583886751593/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#A0A0A0] hover:text-[#00FFFF] hover:bg-white/10 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/modern_path_ai/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#A0A0A0] hover:text-[#00FFFF] hover:bg-white/10 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://share.google/7RsHcHJ6UgZ36oxK1" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#A0A0A0] hover:text-[#00FFFF] hover:bg-white/10 transition-colors"
                aria-label="Google"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </a>
            </div>
          </motion.div>

          {/* Our Solutions Column */}
          <motion.div variants={fadeUp}>
            <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">Our Solutions</h4>
            <ul className="space-y-3">
              {priorityIndustries.map((industry) => (
                <li key={industry.slug}>
                  <Link 
                    to={`/industries/${industry.slug}`}
                    className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                  >
                    AI for {industry.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/industries/home-services"
                  className="text-sm text-[#00FFFF] hover:underline"
                >
                  View All Industries →
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Quick Links Column */}
          <motion.div variants={fadeUp}>
            <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <a 
                  href={CALENDLY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                >
                  Book a Demo
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a href="/sitemap.xml" className="text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors">
                  Sitemap
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Contact Column */}
          <motion.div variants={fadeUp}>
            <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:info@modernpathai.com" 
                  className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  info@modernpathai.com
                </a>
              </li>
              <li>
                <a 
                  href="tel:+16473707545" 
                  className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#00FFFF] transition-colors"
                >
                  <Phone className="w-4 h-4 shrink-0" />
                  647-370-7545
                </a>
              </li>
              <li className="text-sm text-[#606060] pt-2">
                <p>Toronto, Ontario</p>
                <p>Canada</p>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Bottom Section */}
        <motion.div 
          variants={fadeUp}
          className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-[#606060] text-sm">
            © {new Date().getFullYear()} Modern Path AI Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-[#606060] hover:text-[#00FFFF] transition-colors">
              Privacy Policy
            </Link>
            <a href="/sitemap.xml" className="text-sm text-[#606060] hover:text-[#00FFFF] transition-colors">
              Sitemap
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default GlobalFooter;
export { priorityIndustries, CALENDLY_LINK };
